"use client";

import { textFieldStyle } from "@/components/AuthForms/authStyles";
import { useAuth } from "@/contexts/AuthContext";
import { useGoogleAuth } from "@/contexts/GoogleAuthProvider";
import { useAuthFormValidation } from "@/hooks/useAuthFormValidation";
import { FORM_TYPES } from "@/utils";
import { createErrorAdornment, createPasswordFieldStyles, createPasswordVisibilityToggle } from "@/utils/authUtils";
import { validateEmail, validatePassword } from "@/utils/validation";
import { VALIDATION_MESSAGES } from "@/utils/validationMessages";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface AuthDialogProps {
  initialMode?: typeof FORM_TYPES[keyof typeof FORM_TYPES];
  onClose?: () => void;
  onSuccess?: () => void;
}

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  marketingOptIn: boolean;
}

interface ForgotPasswordFormData {
  email: string;
}

export default function AuthDialog({
  initialMode = FORM_TYPES.LOGIN,
  onClose,
  onSuccess,
}: AuthDialogProps) {
  const [mode, setMode] = useState<typeof FORM_TYPES[keyof typeof FORM_TYPES]>(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { login, register, forgotPassword, googleAuth } = useAuth();
  const { isAvailable: isGoogleAuthAvailable } = useGoogleAuth();

  const validateLoginForm = useMemo(() => (formData: LoginFormData): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = VALIDATION_MESSAGES.LOGIN_EMAIL_REQUIRED;
    } else if (!validateEmail(formData.email)) {
      errors.email = VALIDATION_MESSAGES.EMAIL_INVALID;
    }

    if (!formData.password) {
      errors.password = VALIDATION_MESSAGES.LOGIN_PASSWORD_REQUIRED;
    }

    return errors;
  }, []);

  const validateRegisterForm = useMemo(() => (formData: RegisterFormData): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = VALIDATION_MESSAGES.FIRST_NAME_REQUIRED;
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = VALIDATION_MESSAGES.FIRST_NAME_MIN_LENGTH;
    }

    if (!formData.lastName.trim()) {
      errors.lastName = VALIDATION_MESSAGES.LAST_NAME_REQUIRED;
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = VALIDATION_MESSAGES.LAST_NAME_MIN_LENGTH;
    }

    if (!formData.email) {
      errors.email = VALIDATION_MESSAGES.EMAIL_REQUIRED;
    } else if (!validateEmail(formData.email)) {
      errors.email = VALIDATION_MESSAGES.EMAIL_INVALID;
    }

    if (!formData.password) {
      errors.password = VALIDATION_MESSAGES.PASSWORD_REQUIRED;
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        errors.password = passwordValidation.errors[0].message;
      }
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = VALIDATION_MESSAGES.CONFIRM_PASSWORD_REQUIRED;
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH;
    }

    return errors;
  }, []);

  const validateForgotPasswordForm = useMemo(() => (formData: ForgotPasswordFormData): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = VALIDATION_MESSAGES.EMAIL_REQUIRED;
    } else if (!validateEmail(formData.email)) {
      errors.email = VALIDATION_MESSAGES.EMAIL_INVALID;
    }

    return errors;
  }, []);

  const loginInitialState = useMemo(() => ({
    email: "",
    password: "",
    remember: false,
  }), []);

  const registerInitialState = useMemo(() => ({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    marketingOptIn: false,
  }), []);

  const forgotPasswordInitialState = useMemo(() => ({
    email: "",
  }), []);

  const loginForm = useAuthFormValidation<LoginFormData>({
    initialState: loginInitialState,
    validateForm: validateLoginForm
  });

  const registerForm = useAuthFormValidation<RegisterFormData>({
    initialState: registerInitialState,
    validateForm: validateRegisterForm
  });

  const forgotPasswordForm = useAuthFormValidation<ForgotPasswordFormData>({
    initialState: forgotPasswordInitialState,
    validateForm: validateForgotPasswordForm
  });

  const formRefs = useRef({ loginForm, registerForm, forgotPasswordForm });
  formRefs.current = { loginForm, registerForm, forgotPasswordForm };

  useEffect(() => {
    formRefs.current.loginForm.resetForm();
    formRefs.current.registerForm.resetForm();
    formRefs.current.forgotPasswordForm.resetForm();
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setSuccess(null);
  }, [mode]);

  const handleClose = () => {
    onSuccess?.();
    onClose?.();
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    try {
      if (credentialResponse.credential) {
        const response = await googleAuth(credentialResponse.credential);
        if (response.success) {
          handleClose();
        } else {
          setError(response.error || "Google authentication failed");
        }
      }
    } catch (error) {
      console.error("Google auth error:", error);
      setError("Google authentication failed");
    }
  };

  const handleGoogleError = () => {
    setError("Google authentication failed");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!loginForm.validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await login(loginForm.formData.email, loginForm.formData.password, loginForm.formData.remember);

      if (response.success) {
        handleClose();
      } else {
        setError(response.error || "Invalid credentials. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An error occurred during login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!registerForm.validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await register({
        email: registerForm.formData.email,
        newPassword: registerForm.formData.password,
        confirmPassword: registerForm.formData.confirmPassword,
        firstName: registerForm.formData.firstName,
        lastName: registerForm.formData.lastName,
        marketingOptIn: registerForm.formData.marketingOptIn,
      });

      if (response.success) {
        handleClose();
      } else {
        setError(response.error || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      setError("An error occurred during registration. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!forgotPasswordForm.validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await forgotPassword(forgotPasswordForm.formData.email);

      if (response.success) {
        setSuccess("Password reset email sent. Please check your inbox.");
        forgotPasswordForm.resetForm();
      } else {
        setError(response.error || "Failed to send password reset email. Please try again.");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoginForm = () => {

    return (
      <form onSubmit={handleLogin} noValidate className="space-y-4">
        <div>
          <TextField
            type="email"
            name="email"
            label="Email Address"
            variant="outlined"
            margin="normal"
            fullWidth
            value={loginForm.formData.email}
            onChange={(e) => loginForm.handleInputChange("email", e.target.value)}
            error={!!loginForm.getFieldError("email")}
            helperText={loginForm.getFieldError("email")}
            InputProps={{
              endAdornment: createErrorAdornment(!!loginForm.getFieldError("email")),
            }}
            sx={textFieldStyle}
          />
        </div>

        <div>
          <TextField
            type="text" // Always use text type for universal browser compatibility
            name="password"
            label="Password"
            variant="outlined"
            margin="normal"
            fullWidth
            value={loginForm.formData.password}
            onChange={(e) => loginForm.handleInputChange("password", e.target.value)}
            error={!!loginForm.getFieldError("password")}
            helperText={loginForm.getFieldError("password")}
            InputProps={{
              endAdornment: createErrorAdornment(
                !!loginForm.getFieldError("password"),
                createPasswordVisibilityToggle(
                  showPassword,
                  () => setShowPassword(!showPassword),
                  !!loginForm.formData.password
                )
              ),
            }}
            sx={createPasswordFieldStyles(showPassword, textFieldStyle)}
          />

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setMode(FORM_TYPES.FORGOT_PASSWORD)}
              className="text-black hover:text-light-grey text-xs"
            >
              Forgot password?
            </button>
          </div>
        </div>

        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={loginForm.formData.remember}
                onChange={(e) => loginForm.handleInputChange("remember", e.target.checked)}
              />
            }
            label={<span className="font-raleway text-xs text-black">Remember me</span>}
          />
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-white text-black py-3 font-raleway text-sm font-bold border border-black hover:bg-light-grey disabled:opacity-50"
        >
          {isLoading ? "SIGNING IN..." : "SIGN IN"}
        </button>

        <div className="flex items-center my-4">
          <div className="flex-grow border-t border-light-grey"></div>
          <span className="flex-shrink mx-4 text-light-grey">or</span>
          <div className="flex-grow border-t border-light-grey"></div>
        </div>

        {isGoogleAuthAvailable ? (
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="outline"
            size="large"
            width="100%"
            text="continue_with"
          />
        ) : (
          <div className="w-full h-12 border border-gray-300 rounded flex items-center justify-center bg-gray-100">
            <span className="text-sm text-gray-500">
              Google Sign-In unavailable
            </span>
          </div>
        )}
      </form>
    );
  };

  const renderRegisterForm = () => {

    return (
      <form onSubmit={handleRegister} noValidate className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            type="text"
            name="firstName"
            label="First Name"
            variant="outlined"
            margin="normal"
            fullWidth
            value={registerForm.formData.firstName}
            onChange={(e) => registerForm.handleInputChange("firstName", e.target.value)}
            error={!!registerForm.getFieldError("firstName")}
            helperText={registerForm.getFieldError("firstName")}
            InputProps={{
              endAdornment: createErrorAdornment(!!registerForm.getFieldError("firstName")),
            }}
            sx={textFieldStyle}
          />

          <TextField
            type="text"
            name="lastName"
            label="Last Name"
            variant="outlined"
            margin="normal"
            fullWidth
            value={registerForm.formData.lastName}
            onChange={(e) => registerForm.handleInputChange("lastName", e.target.value)}
            error={!!registerForm.getFieldError("lastName")}
            helperText={registerForm.getFieldError("lastName")}
            InputProps={{
              endAdornment: createErrorAdornment(!!registerForm.getFieldError("lastName")),
            }}
            sx={textFieldStyle}
          />
        </div>

        <div>
          <TextField
            type="email"
            name="email"
            label="Email Address"
            variant="outlined"
            margin="normal"
            fullWidth
            value={registerForm.formData.email}
            onChange={(e) => registerForm.handleInputChange("email", e.target.value)}
            error={!!registerForm.getFieldError("email")}
            helperText={registerForm.getFieldError("email")}
            InputProps={{
              endAdornment: createErrorAdornment(!!registerForm.getFieldError("email")),
            }}
            sx={textFieldStyle}
          />
        </div>

        <div>
          <TextField
            type="text" // Always use text type for universal browser compatibility
            name="password"
            label="Password"
            variant="outlined"
            margin="normal"
            fullWidth
            value={registerForm.formData.password}
            onChange={(e) => registerForm.handleInputChange("password", e.target.value)}
            error={!!registerForm.getFieldError("password")}
            helperText={registerForm.getFieldError("password")}
            InputProps={{
              endAdornment: createErrorAdornment(
                !!registerForm.getFieldError("password"),
                createPasswordVisibilityToggle(
                  showPassword,
                  () => setShowPassword(!showPassword),
                  !!registerForm.formData.password
                )
              ),
            }}
            sx={createPasswordFieldStyles(showPassword, textFieldStyle)}
          />
        </div>

        <div>
          <TextField
            type="text" // Always use text type for universal browser compatibility
            name="confirmPassword"
            label="Confirm Password"
            variant="outlined"
            margin="normal"
            fullWidth
            value={registerForm.formData.confirmPassword}
            onChange={(e) => registerForm.handleInputChange("confirmPassword", e.target.value)}
            error={!!registerForm.getFieldError("confirmPassword")}
            helperText={registerForm.getFieldError("confirmPassword")}
            InputProps={{
              endAdornment: createErrorAdornment(
                !!registerForm.getFieldError("confirmPassword"),
                createPasswordVisibilityToggle(
                  showConfirmPassword,
                  () => setShowConfirmPassword(!showConfirmPassword),
                  !!registerForm.formData.confirmPassword
                )
              ),
            }}
            sx={createPasswordFieldStyles(showConfirmPassword, textFieldStyle)}
          />
        </div>

        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={registerForm.formData.marketingOptIn}
                onChange={(e) => registerForm.handleInputChange("marketingOptIn", e.target.checked)}
              />
            }
            label={<span className="font-raleway text-xs text-black">I want to receive marketing communications</span>}
          />
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-white text-black py-3 font-raleway text-sm font-bold border border-black hover:bg-light-grey disabled:opacity-50"
        >
          {isLoading ? "CREATING ACCOUNT..." : "CREATE ACCOUNT"}
        </button>

        <div className="flex items-center my-4">
          <div className="flex-grow border-t border-light-grey"></div>
          <span className="flex-shrink mx-4 text-light-grey">or</span>
          <div className="flex-grow border-t border-light-grey"></div>
        </div>

        {isGoogleAuthAvailable ? (
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="outline"
            size="large"
            width="100%"
            text="signup_with"
          />
        ) : (
          <div className="w-full h-12 border border-gray-300 rounded flex items-center justify-center bg-gray-100">
            <span className="text-sm text-gray-500">
              Google Sign-Up unavailable
            </span>
          </div>
        )}

        <p className="text-xs text-black text-center">
          By registering, you agree to our{" "}
          <a href="/terms" className="underline">
            Terms of Use
          </a>{" "}
          and{" "}
          <a href="/privacy" className="underline">
            Privacy Policy
          </a>
        </p>
      </form>
    );
  };

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} noValidate className="space-y-4">
      <div>
        <TextField
          type="email"
          name="email"
          label="Email Address"
          variant="outlined"
          margin="normal"
          fullWidth
          value={forgotPasswordForm.formData.email}
          onChange={(e) => forgotPasswordForm.handleInputChange("email", e.target.value)}
          error={!!forgotPasswordForm.getFieldError("email")}
          helperText={forgotPasswordForm.getFieldError("email")}
          InputProps={{
            endAdornment: createErrorAdornment(!!forgotPasswordForm.getFieldError("email")),
          }}
          sx={textFieldStyle}
        />
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      {success && <div className="text-green-600 text-sm">{success}</div>}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-white text-black py-3 font-raleway text-sm font-bold border border-black hover:bg-light-grey disabled:opacity-50"
      >
        {isLoading ? "SENDING..." : "SEND RESET EMAIL"}
      </button>

      <button
        type="button"
        onClick={() => setMode(FORM_TYPES.LOGIN)}
        className="w-full text-black hover:text-light-grey text-sm"
      >
        Back to Login
      </button>
    </form>
  );

  const getForm = () => {
    switch (mode) {
      case FORM_TYPES.LOGIN:
        return renderLoginForm();
      case FORM_TYPES.REGISTER:
        return renderRegisterForm();
      case FORM_TYPES.FORGOT_PASSWORD:
        return renderForgotPasswordForm();
      default:
        return renderLoginForm();
    }
  };

  return (
    <div className="bg-white w-full max-w-md min-w-md px-4 pt-10 pb-4">
      <div className="mx-6 border-b border-light-grey font-raleway text-md">
        <div className="flex gap-2">
          <button
            onClick={() => setMode(FORM_TYPES.LOGIN)}
            className={`pb-3 px-2 text-sm font-medium transition-colors ${mode === FORM_TYPES.LOGIN
              ? "text-black border-b-2 border-black"
              : "text-light-grey hover:text-black"
              }`}
          >
            Login
          </button>
          <button
            onClick={() => setMode(FORM_TYPES.REGISTER)}
            className={`pb-3 px-2 text-sm font-medium transition-colors ${mode === FORM_TYPES.REGISTER
              ? "text-black border-b-2 border-black"
              : "text-light-grey hover:text-black"
              }`}
          >
            Register
          </button>
        </div>
      </div>

      <div className="mx-6 mt-6">
        {getForm()}
      </div>
    </div>
  );
}
